'use client';

import {
  Accordion as AccordionComp,
  AccordionContent,
  AccordionItem,
} from '@repo/ui/components/ui/accordion';
import { Button } from '@repo/ui/components/ui/button';
import { Card } from '@repo/ui/components/ui/card';
import { ChevronDown } from '@repo/ui/components/ui/icons';
import { PetTag } from '@repo/ui/components/ui/pet-tag';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@repo/ui/components/ui/sheet';
import { useRef, useState } from 'react';
import { BookingModal } from '../booking-modal';
import { motion, AnimatePresence } from 'framer-motion';
import React from 'react';

type ServiceVariantType = {
  id: string;
  variant_name: string;
  price: number;
  status: string;
};

type IServiceCardProps = {
  detail: string;
  service_name: string;
  service_about: string;
  service_variant?: ServiceVariantType[];
  pet_type: string;
  price?: string | number | undefined;
  line_url?: string;
  onClick?: () => void;
};

const DEFAULT_LINE_URL = 'https://lin.ee/39EJlkv';
const ModationBtn = motion(Button);
export const BusinessService = ({
  detail,
  service_name,
  service_about,
  line_url = DEFAULT_LINE_URL,
  pet_type,
  service_variant = [
    { id: '1', variant_name: '', price: 0, status: 'available' },
  ],
}: IServiceCardProps) => {
  const lowestPriceServiceVariant = service_variant.reduce((prev, current) => {
    return prev.price < current.price ? prev : current;
  });
  const user = true;
  const [showModal, setShowModal] = useState(false);
  const [showSheet, setShowSheet] = useState(false);
  const [collapsing, setCollapsing] = useState('');

  const redirectUrlRef = useRef<string | undefined>(undefined);

  if (!user) {
    return <Button onClick={() => setShowModal(true)}>Login</Button>;
  }

  return (
    <>
      <Card className="space-y-3 p-6">
        <div className="flex justify-end gap-4 max-sm:flex-wrap lg:gap-12">
          <div className="flex-grow space-y-4">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1">
                {pet_type && <PetTag type={pet_type} />}
                <p className="text-b2 font-bold">{service_name}</p>
              </div>
              <p className="text-muted-foreground text-base">{service_about}</p>
            </div>
          </div>
          <div
            className="flex h-fit min-w-[147px] flex-col items-end justify-end gap-3"
            dir="rtl"
          >
            <AnimatePresence mode="popLayout" initial={false}>
              {collapsing ? (
                <div className="w-full lg:h-[75px]">
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex w-full flex-row-reverse items-center gap-2 font-medium"
                  >
                    <div className="text-b2 w-full text-nowrap">Show less</div>
                    <Button
                      onClick={() =>
                        setCollapsing(collapsing ? '' : 'package-detail')
                      }
                      size={'sm'}
                      variant={'outline'}
                      className="p-2"
                    >
                      <ChevronDown className="h-4 w-4 shrink-0 rotate-180 transition-transform duration-200" />
                    </Button>
                  </motion.div>
                </div>
              ) : (
                <div className="w-full space-y-2">
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-b1 w-full text-nowrap font-bold"
                  >
                    ราคา {lowestPriceServiceVariant.price.toLocaleString()} THB
                  </motion.p>
                  <div className="w-full">
                    {service_variant.length > 1 ? (
                      <ModationBtn
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ delay: 0.1 }}
                        className="w-[150px]"
                        onClick={() =>
                          setCollapsing(collapsing ? '' : 'package-detail')
                        }
                      >
                        {`${service_variant.length.toString()} Packages`}
                      </ModationBtn>
                    ) : (
                      <Button
                        className="w-[150px]"
                        onClick={() => setShowModal(true)}
                      >
                        Contact
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div>
          <div
            onClick={() => setShowSheet(true)}
            className="text-info-foreground hidden w-fit cursor-pointer text-sm duration-300 active:opacity-70 lg:block"
          >
            More details →
          </div>
          <div className="space-y-2">
            <AccordionComp
              type="single"
              collapsible
              className=""
              value={collapsing}
              onValueChange={setCollapsing}
            >
              <AccordionItem value={'package-detail'} className="border-b-0">
                <AccordionContent className="space-y-3 border-b-0 py-4">
                  {service_variant &&
                    service_variant.length >= 1 &&
                    service_variant.map((value, index) => {
                      return (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ delay: 0.1 }}
                          key={index}
                          className="bg-secondary text-b3 flex w-full items-center justify-between rounded-lg p-3 font-medium"
                        >
                          <p>{value.variant_name}</p>
                          <p>THB {value.price}</p>
                        </motion.div>
                      );
                    })}

                  {collapsing && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ delay: 0.1 }}
                      className="flex justify-end gap-4"
                    >
                      <ModationBtn
                        className="w-[150px]"
                        onClick={() => setShowModal(true)}
                      >
                        Contact
                      </ModationBtn>
                    </motion.div>
                  )}
                </AccordionContent>
              </AccordionItem>
            </AccordionComp>
          </div>
          <div
            onClick={() => setShowSheet(true)}
            className="text-info-foreground w-fit cursor-pointer text-sm duration-300 active:opacity-70 lg:hidden"
          >
            More details →
          </div>
        </div>
      </Card>

      <BookingModal
        showModal={showModal}
        setShowModal={(val) => {
          setShowModal(val);
          redirectUrlRef.current = undefined;
        }}
        lineUrl={line_url ?? DEFAULT_LINE_URL}
      />

      <Sheet
        open={showSheet}
        onOpenChange={(visible) => {
          if (!visible) {
            setShowSheet(false);
          }
        }}
      >
        <SheetContent
          showCloseButton={false}
          className="w-full !max-w-[696px] space-y-8 lg:!w-[696px]"
        >
          <SheetClose>
            <Button variant={'secondary'}>→ Close</Button>
          </SheetClose>
          <SheetHeader className="space-y-8">
            <div className="space-y-2">
              <SheetTitle>{service_name}</SheetTitle>
              <SheetDescription>
                <div>{service_about}</div>
              </SheetDescription>
            </div>
            <div className="space-y-2">
              <p className="text-b2 font-semibold">About this services</p>
              <SheetDescription>
                <div
                  className={'no-tailwindcss-base text-sm'}
                  dangerouslySetInnerHTML={{ __html: detail }}
                />
              </SheetDescription>
            </div>
          </SheetHeader>
        </SheetContent>
      </Sheet>
    </>
  );
};
